import type {
  EntradaFranja,
  Franja,
  HistorialFranja,
  TasaFranja,
} from '../../services/main-rates.service';
import { TEXTO_HTTP_GENERICO, TEXTO_SESION_VENCIDA } from 'src/app/shared/utils/error-http-real';

/**
 * Reglas puras del editor de franjas CLP↔VES y de las tarjetas «Tasa web del
 * día». Sin Angular ni HTTP, para poder probarlas sin navegador de por medio.
 *
 * El backend es la autoridad: valida huecos, cruces y monotonía, y lo que
 * diga (errores y advertencias) se muestra siempre.
 */

// ───────────────────────── Tarjetas «Tasa web del día» ─────────────────────────

/** Una tarjeta de tasa tal como la pinta main-rates (misma forma que hoy). */
export interface FilaTasaWeb {
  rango: string;
  /** «$15.000 CLP – $99.999 CLP», armado con desde/hasta de `tramos[]`. */
  etiqueta?: string;
  descripcion: string;
  tasa: number | undefined;
  perfil: 'Client' | 'Admin';
}

/**
 * Tarjetas desde `tramos` de `ultimas-agrupadas` (franjas dinámicas).
 *
 * `null` si la respuesta no trae `tramos` o viene vacío: en ese caso la
 * pantalla sigue con `rangoA/B/C`, exactamente como antes.
 */
export function filasWebDesdeTramos(
  body: any,
  perfil: 'Client' | 'Admin' = 'Client',
): FilaTasaWeb[] | null {
  const lista = body?.tramos;
  if (!Array.isArray(lista) || !lista.length) return null;

  const clave = perfil === 'Client' ? 'client' : 'admin';
  const filas = [...lista]
    .filter((t) => t && Number.isFinite(Number(t.desde)) && Number.isFinite(Number(t.hasta)))
    .sort((a, b) => Number(a.desde) - Number(b.desde))
    .map((t) => {
      const fila = t[clave];
      const tasa = Number(fila?.tasa);
      return {
        rango: `${Number(t.desde)}-${Number(t.hasta)}`,
        etiqueta: etiquetaRangoClp(Number(t.desde), Number(t.hasta)),
        descripcion: fila?.descripcion || '',
        tasa: tasa > 0 ? tasa : undefined,
        perfil,
      } as FilaTasaWeb;
    });

  return filas.length ? filas : null;
}

// ───────────────────────── Formato y avisos de la tabla ─────────────────────────

/** Texto del modo solo lectura (editor abierto por quien no edita tasas). */
export const TEXTO_SOLO_LECTURA = 'Solo pueden editar los encargados de tasas.';

/**
 * Margen VES→CLP desde el que se avisa en rojo. Es el espejo de la holgura del
 * piso de franja del back (`HOLGURA_FRANJA_PISO = 0,95` en
 * `validar-tasa-ves-clp.util.ts`): con más de ~5 % de margen, el criterio de
 * respaldo (VES ÷ tasa) deja el monto bajo el piso de su franja y algunos
 * retiros se rechazan. Solo es un aviso: no bloquea el guardado.
 */
export const MARGEN_AVISO_VES_CLP_PCT = 5;
export const TEXTO_PASA_TOPE_VES_CLP =
  'Pasa el 5 %: algunos retiros serían rechazados. Baja el descuento.';

/** ¿La ganancia de una franja VES→CLP llega al tope del aviso? */
export function pasaTopeVesClp(par: 'CLP-VES' | 'VES-CLP', gananciaPct: number | null | undefined): boolean {
  const g = Number(gananciaPct);
  return par === 'VES-CLP' && gananciaPct != null && Number.isFinite(g) && g >= MARGEN_AVISO_VES_CLP_PCT;
}

/** Número en es-CL con `dec` decimales fijos; «—» si no hay número. */
export function formatoNumero(x: number | null | undefined, dec = 2): string {
  const n = Number(x);
  if (x == null || !Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CL', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/**
 * Porcentaje en es-CL con coma y espacio: «1,25 %». `signo` antepone «+» a
 * los positivos (los negativos llevan «−»); `ceroGuion` muestra «—» cuando
 * redondea a 0.
 */
export function formatoPct(
  x: number | null | undefined,
  opciones: { signo?: boolean; ceroGuion?: boolean; dec?: number } = {},
): string {
  const n = Number(x);
  if (x == null || !Number.isFinite(n)) return '—';
  const dec = opciones.dec ?? 2;
  const factor = Math.pow(10, dec);
  const r = Math.round(n * factor) / factor;
  if (r === 0) {
    if (opciones.ceroGuion) return '—';
    return `${(0).toLocaleString('es-CL', { minimumFractionDigits: dec, maximumFractionDigits: dec })} %`;
  }
  const texto = Math.abs(r).toLocaleString('es-CL', { minimumFractionDigits: dec, maximumFractionDigits: dec });
  const signo = r < 0 ? '−' : opciones.signo ? '+' : '';
  return `${signo}${texto} %`;
}

/**
 * Cuánto recibe de menos (negativo) el cliente de la franja frente a la tasa
 * base con comisión, en %. CLP→VES recibe `monto × tasa`; VES→CLP recibe
 * `monto ÷ tasa`. Solo presenta: no cambia ningún cálculo.
 */
export function descuentoVsBase(
  par: 'CLP-VES' | 'VES-CLP',
  tasaCliente: number | null | undefined,
  base: number | null | undefined,
): number | null {
  const t = Number(tasaCliente);
  const b = Number(base);
  if (tasaCliente == null || base == null || !(t > 0) || !(b > 0)) return null;
  return par === 'CLP-VES' ? (t / b - 1) * 100 : (b / t - 1) * 100;
}

/** «HH:MM» local de una fecha (Date o ISO); null si no es válida. */
export function horaCorta(v: Date | string | null | undefined): string | null {
  if (v == null || v === '') return null;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return null;
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ───────────────────────── Espera al cron tras guardar ─────────────────────────

/** Cada cuánto se revisan las tarjetas mientras el guardado no se ve aplicado. */
export const ESPERA_CRON_CADA_MS = 15_000;
/** Cuántas revisiones (~4 min: el cron corre cada 3) antes de avisar que se aplicará después. */
export const ESPERA_CRON_INTENTOS = 16;
/** Tolerancia de redondeo a 4 decimales (r4) al comparar con lo esperado. */
export const TOLERANCIA_R4 = 0.0001;

/** Tasa esperada de una fila de la tarjeta (rango «desde-hasta» y tasa al cliente). */
export interface TasaEsperada {
  rango: string;
  tasa: number;
}

/** Lo que la vista previa oficial dice que se va a grabar, en el formato de la tarjeta. */
export function esperadasDesdeVista(tasas: Array<{ desde: number; hasta: number; client: number }> | null | undefined): TasaEsperada[] {
  return (tasas || [])
    .filter((t) => t && Number.isFinite(Number(t.desde)) && Number.isFinite(Number(t.hasta)) && Number(t.client) > 0)
    .map((t) => ({ rango: `${Number(t.desde)}-${Number(t.hasta)}`, tasa: Number(t.client) }));
}

/**
 * ¿Las tasas grabadas ya son las de lo guardado?
 *  - Mismos rangos que la vista previa y cada tasa a menos de `TOLERANCIA_R4`.
 *  - O mismos rangos y la tarjeta cambió frente a la foto fresca de antes del
 *    PUT: si el mercado se movió entre la vista previa y la corrida del cron,
 *    la tasa no calza al r4 pero lo grabado ya es posterior al guardado.
 * Sin esperadas (no había vista previa al día), basta el cambio frente a la foto.
 */
export function franjasAplicadas(
  filas: Array<{ rango: string; tasa?: number | null }> | null | undefined,
  esperadas: TasaEsperada[] | null | undefined,
  foto: string,
): boolean {
  const actual = huellaFilasWeb(filas);
  if (!esperadas || !esperadas.length) return cambioDesdeFoto(foto, actual);
  const lista = filas || [];
  const rangos = (xs: Array<{ rango: string }>) => xs.map((x) => x.rango).sort().join('|');
  if (lista.length !== esperadas.length || rangos(lista) !== rangos(esperadas)) return false;
  const calzan = esperadas.every((e) => {
    const f = lista.find((x) => x.rango === e.rango);
    return f != null && Math.abs(Number(f.tasa) - e.tasa) <= TOLERANCIA_R4 + 1e-9;
  });
  return calzan || cambioDesdeFoto(foto, actual);
}
export const TEXTO_SE_APLICARA = 'Se aplicará en la próxima actualización';

/**
 * Huella de las tarjetas «Tasa web del día» (rango y tasa de cada fila) para
 * saber si las tasas grabadas cambiaron. Vacía si no hay filas.
 */
export function huellaFilasWeb(filas: Array<{ rango?: string; tasa?: number | null }> | null | undefined): string {
  if (!Array.isArray(filas) || !filas.length) return '';
  return filas.map((f) => `${f?.rango ?? ''}:${f?.tasa ?? ''}`).join('|');
}

/**
 * Filas de la tarjeta a partir de la respuesta de las últimas tasas agrupadas,
 * con la misma lectura que usa main-rates: CLP→VES (`rates/ultimas-agrupadas`,
 * `tramos` en la raíz o en `data`) y VES→CLP (`rates/ves-clp/ultimas-agrupadas`,
 * `tramos` o los tres rangos de siempre).
 */
export function filasTarjetaDesde(par: 'CLP-VES' | 'VES-CLP', r: any): Array<{ rango: string; tasa?: number | null }> | null {
  if (par === 'CLP-VES') {
    const body = Array.isArray(r?.tramos) ? r : r?.data;
    return filasWebDesdeTramos(body, 'Client');
  }
  if (!r) return null;
  const dinamicas = filasWebDesdeTramos(r, 'Client');
  if (dinamicas) return dinamicas;
  return ['rangoA', 'rangoB', 'rangoC'].map((k) => ({
    rango: r?.[k]?.client?.rango || '',
    tasa: r?.[k]?.client?.tasa,
  }));
}

/**
 * ¿Ya se ve aplicado lo guardado? Las tasas grabadas cambiaron frente a la
 * foto de antes de guardar. Cualquier grabación posterior al guardado ya usa
 * las franjas nuevas (el back las lee después de guardar), así que un cambio
 * alcanza. Sin foto (las tarjetas no habían cargado) no se puede saber: false.
 */
export function cambioDesdeFoto(foto: string, actual: string): boolean {
  return !!foto && !!actual && foto !== actual;
}

// ───────────────────────── Editor ─────────────────────────
//
// Se mantiene la fórmula de siempre (modo DESCUENTOS): la franja más grande
// usa la tasa base con la comisión y cada franja más chica lleva un descuento
// más (DESCUENTO_FIAT). Con N franjas activas: N-1, N-2, …, 0.
//
// Los cortes se encadenan solos: «desde» = «hasta» de la anterior + 1, la
// primera arranca en el mínimo y la última termina en el máximo. Así nunca
// queda hueco ni cruce.

/** Tope del back para los pasos de DESCUENTOS. */
export const DESCUENTOS_MAX = 20;

export const MOTIVO_MIN = 5;
export const MOTIVO_MAX = 255;

export const AYUDA_DESCUENTOS =
  'Cada franja más chica lleva un descuento más (DESCUENTO_FIAT). La más grande usa la tasa base con la comisión.';

export const TEXTO_NEGATIVA = 'Esta franja paga más que el mercado';

/** Una franja activa en edición. */
export interface FilaDescuentos {
  /** Ausente = franja nueva (se manda sin clave). */
  clave?: string;
  letra: string;
  /** Sólo se usa en las que no son la última (la última termina en el máximo). */
  hasta: number | null;
  /** Pasos enteros de DESCUENTO_FIAT (0–20). */
  descuentos: number | null;
}

export interface Borrador {
  minimo: number | null;
  maximo: number | null;
  /** Activas, en orden de monto. */
  filas: FilaDescuentos[];
  /** Apagadas (las que ya venían y las quitadas acá). Se mandan con su valor actual y activo=false. */
  apagadas: Franja[];
}

/** Letra por posición, como la rotula el backend (A, B, C…). */
export function letraDe(i: number): string {
  return i < 26 ? String.fromCharCode(65 + i) : String(i + 1);
}

export function reletrar(filas: FilaDescuentos[]): FilaDescuentos[] {
  return filas.map((f, i) => ({ ...f, letra: letraDe(i) }));
}

export function round4(x: number): number {
  return Math.round(x * 1e4) / 1e4;
}

/** Descuentos automáticos por posición: N-1, N-2, …, 0 (la más grande, 0). */
export function descuentosAutomaticos(n: number): number[] {
  return Array.from({ length: Math.max(0, n) }, (_, i) => n - 1 - i);
}

/** Pone los descuentos automáticos en todas las franjas activas. */
export function aplicarAutomatico(b: Borrador): Borrador {
  const auto = descuentosAutomaticos(b.filas.length);
  return { ...b, filas: b.filas.map((f, i) => ({ ...f, descuentos: auto[i] })) };
}

/** ¿Los descuentos de la fila son los automáticos de su posición? */
export function esAutomatico(b: Borrador, i: number): boolean {
  return b.filas[i]?.descuentos === b.filas.length - 1 - i;
}

/**
 * Borrador desde lo que devuelve GET rates/franjas/:par. Las franjas en
 * DESCUENTOS conservan su valor guardado; las que estén en otro modo toman el
 * automático de su posición (al guardar pasan a DESCUENTOS).
 */
export function borradorDesde(franjas: Franja[] | null | undefined): Borrador {
  const todas = [...(franjas || [])].sort((a, b) => a.desde - b.desde || a.orden - b.orden);
  const activas = todas.filter((f) => f.activo);
  const apagadas = todas.filter((f) => !f.activo).map((f) => ({ ...f }));
  const auto = descuentosAutomaticos(activas.length);

  const filas = activas.map((f, i) => {
    const a = Number(f.ajuste);
    const guardado = f.modo === 'DESCUENTOS' && Number.isInteger(a) && a >= 0;
    return {
      clave: f.clave,
      letra: letraDe(i),
      hasta: f.hasta,
      descuentos: guardado ? a : auto[i],
    };
  });

  return {
    minimo: activas.length ? activas[0].desde : null,
    maximo: activas.length ? activas[activas.length - 1].hasta : null,
    filas,
    apagadas,
  };
}

/** «Desde» encadenado: la primera = mínimo, el resto = hasta de la anterior + 1. */
export function desdeDe(b: Borrador, i: number): number | null {
  if (i === 0) return b.minimo;
  const prev = b.filas[i - 1]?.hasta;
  return prev == null ? null : Number(prev) + 1;
}

/** «Hasta» efectivo: la última termina en el máximo. */
export function hastaDe(b: Borrador, i: number): number | null {
  return i === b.filas.length - 1 ? b.maximo : b.filas[i]?.hasta ?? null;
}

export function esUltima(b: Borrador, i: number): boolean {
  return i === b.filas.length - 1;
}

/**
 * Lo que se manda: SIEMPRE el conjunto completo. Activas en DESCUENTOS con su
 * entero; apagadas con su clave, su valor actual y activo=false.
 */
export function entradaDesdeBorrador(b: Borrador): EntradaFranja[] {
  const activas: EntradaFranja[] = b.filas.map((f, i) => {
    const e: EntradaFranja = {
      desde: Math.round(Number(desdeDe(b, i))),
      hasta: Math.round(Number(hastaDe(b, i))),
      modo: 'DESCUENTOS',
      ajuste: Number(f.descuentos),
      activo: true,
    };
    if (f.clave) e.clave = f.clave;
    return e;
  });
  const apagadas: EntradaFranja[] = b.apagadas.map((f) => ({
    clave: f.clave,
    desde: f.desde,
    hasta: f.hasta,
    modo: f.modo,
    ajuste: Number(f.ajuste),
    activo: false,
  }));
  return [...activas, ...apagadas];
}

/** Problemas que se ven sin preguntarle al servidor. No reemplazan su validación. */
export function erroresBorrador(b: Borrador): string[] {
  const errores: string[] = [];
  const min = Number(b.minimo);
  const max = Number(b.maximo);
  if (b.minimo == null || !Number.isInteger(min) || min < 1) {
    errores.push('El monto mínimo tiene que ser un número entero de pesos mayor que 0.');
  }
  if (b.maximo == null || !Number.isInteger(max) || max < 1) {
    errores.push('El monto máximo tiene que ser un número entero de pesos mayor que 0.');
  } else if (Number.isFinite(min) && max <= min) {
    errores.push('El monto máximo tiene que ser mayor que el mínimo.');
  }
  if (!b.filas.length) errores.push('Tiene que haber al menos una franja.');

  b.filas.forEach((f, i) => {
    const nombre = `Franja ${f.letra}`;
    if (!esUltima(b, i)) {
      const h = Number(f.hasta);
      const d = desdeDe(b, i);
      if (f.hasta == null || !Number.isInteger(h) || h < 1) {
        errores.push(`${nombre}: «Hasta» tiene que ser un número entero de pesos.`);
      } else if (d != null && h < d) {
        errores.push(`${nombre}: «Hasta» no puede ser menor que «Desde» (${d}).`);
      } else if (Number.isFinite(max) && h >= max) {
        errores.push(`${nombre}: «Hasta» tiene que ser menor que el monto máximo.`);
      }
    }
    const n = Number(f.descuentos);
    if (f.descuentos == null || !Number.isInteger(n) || n < 0 || n > DESCUENTOS_MAX) {
      errores.push(`${nombre}: los descuentos son un número entero de 0 a ${DESCUENTOS_MAX}.`);
    }
  });
  return errores;
}

export interface Resultado {
  borrador: Borrador;
  error: string | null;
}

/**
 * Agregar y quitar. `automatico` (por defecto sí): vuelve a poner los
 * descuentos por posición (N-1…0), como siempre. Con el motor MARGENES va en
 * false: los descuentos no rigen (el back los ignora), así que la franja nueva
 * va con 0 y las demás conservan los suyos.
 */
export interface OpcionesEdicion {
  automatico?: boolean;
}

/**
 * Corte sugerido para la que hoy es la última al agregar una franja: el punto
 * medio entre su «desde» y el máximo, redondeado a miles. Si el redondeo se
 * sale del rango, el punto medio exacto. `null` si no hay espacio.
 */
export function corteSugeridoUltima(b: Borrador): number | null {
  const i = b.filas.length - 1;
  const d = desdeDe(b, i);
  const max = b.maximo;
  if (i < 0 || d == null || max == null || !(max > d)) return null;
  const medio = Math.floor((d + max) / 2);
  const redondo = Math.round(medio / 1000) * 1000;
  return redondo >= d && redondo < max ? redondo : medio;
}

/**
 * Agrega una franja AL FINAL. La que era la última deja de terminar en el
 * máximo y su «hasta» pasa al corte sugerido (editable); la nueva va de ese
 * corte + 1 hasta el máximo. Los descuentos se recalculan solos (N-1…0),
 * salvo con `automatico: false` (MARGENES): la nueva va con 0.
 */
export function agregarAlFinal(b: Borrador, opciones: OpcionesEdicion = {}): Resultado {
  if (!b.filas.length) {
    return { borrador: b, error: 'Primero tiene que haber una franja.' };
  }
  const corte = corteSugeridoUltima(b);
  if (corte == null) {
    return {
      borrador: b,
      error: 'No hay espacio para otra franja: revisa el monto mínimo, el máximo y los cortes.',
    };
  }
  const automatico = opciones.automatico !== false;
  const filas = [...b.filas];
  const ultima = filas.length - 1;
  filas[ultima] = { ...filas[ultima], hasta: corte };
  filas.push({ letra: '', hasta: null, descuentos: automatico ? null : 0 });
  const nuevo = { ...b, filas: reletrar(filas) };
  return { borrador: automatico ? aplicarAutomatico(nuevo) : nuevo, error: null };
}

/**
 * Quita la franja `i`: su rango lo absorbe la anterior (o la siguiente si es
 * la primera). Si ya existía en el servidor se apaga (se manda con su clave y
 * activo=false); nunca se borra. Una nueva sin guardar simplemente desaparece.
 * Después los descuentos se vuelven a poner automáticos (salvo con
 * `automatico: false`, en MARGENES: las demás conservan los suyos).
 */
export function quitar(
  b: Borrador,
  i: number,
  originales: Franja[] | null | undefined,
  opciones: OpcionesEdicion = {},
): Resultado {
  const f = b.filas[i];
  if (!f) return { borrador: b, error: null };
  if (b.filas.length <= 1) {
    return { borrador: b, error: 'Tiene que quedar al menos una franja.' };
  }
  const filas = [...b.filas];
  if (i > 0) {
    // La anterior absorbe el rango: toma el «hasta» de la quitada.
    filas[i - 1] = { ...filas[i - 1], hasta: f.hasta };
  }
  // Si es la primera, la siguiente arranca sola en el mínimo (desde encadenado).
  filas.splice(i, 1);

  const apagadas = [...b.apagadas];
  if (f.clave) {
    const orig = (originales || []).find((o) => o.clave === f.clave);
    if (orig) apagadas.push({ ...orig, activo: false });
  }
  const nuevo = { ...b, filas: reletrar(filas), apagadas };
  return {
    borrador: opciones.automatico === false ? nuevo : aplicarAutomatico(nuevo),
    error: null,
  };
}

/** ¿El borrador difiere del que se cargó? */
export function hayCambios(b: Borrador | null, inicial: Borrador | null): boolean {
  if (!b || !inicial) return false;
  return (
    JSON.stringify(entradaDesdeBorrador(b)) !== JSON.stringify(entradaDesdeBorrador(inicial))
  );
}

/** Copia profunda para editar sin tocar el borrador inicial. */
export function clonar(b: Borrador): Borrador {
  return {
    minimo: b.minimo,
    maximo: b.maximo,
    filas: b.filas.map((f) => ({ ...f })),
    apagadas: b.apagadas.map((f) => ({ ...f })),
  };
}

// ───────────────────────── Validación en línea ─────────────────────────
// Mensajes cortos bajo cada campo, sin esperar a ningún botón.

function esEnteroPositivo(v: any): boolean {
  const n = Number(v);
  return v != null && v !== '' && Number.isInteger(n) && n >= 1;
}

export function errorMinimo(b: Borrador): string | null {
  if (!esEnteroPositivo(b.minimo)) return 'Entero mayor que 0, sin decimales.';
  return null;
}

export function errorMaximo(b: Borrador): string | null {
  if (!esEnteroPositivo(b.maximo)) return 'Entero mayor que 0, sin decimales.';
  if (esEnteroPositivo(b.minimo) && Number(b.maximo) <= Number(b.minimo)) {
    return 'Tiene que ser mayor que el mínimo.';
  }
  return null;
}

/** Error del «Hasta» de la fila `i` (la última no se edita: termina en el máximo). */
export function errorHasta(b: Borrador, i: number): string | null {
  if (esUltima(b, i)) return null;
  const f = b.filas[i];
  if (!f) return null;
  if (!esEnteroPositivo(f.hasta)) return 'Entero mayor que 0, sin decimales.';
  const h = Number(f.hasta);
  const d = desdeDe(b, i);
  if (d != null && h < d) return 'No puede ser menor que Desde.';
  if (esEnteroPositivo(b.maximo) && h >= Number(b.maximo)) {
    return 'Tiene que ser menor que el monto máximo.';
  }
  return null;
}

export function errorDescuentos(f: FilaDescuentos): string | null {
  const n = Number(f.descuentos);
  if (f.descuentos == null || !Number.isInteger(n) || n < 0 || n > DESCUENTOS_MAX) {
    return `Entero de 0 a ${DESCUENTOS_MAX}.`;
  }
  return null;
}

// ───────────────────────── Barra de franjas ─────────────────────────

export interface SegmentoBarra {
  letra: string;
  desde: number;
  hasta: number;
  /** Ancho en % del total (suman 100). */
  ancho: number;
}

/**
 * Un segmento por franja, con ancho proporcional en escala logarítmica entre
 * el mínimo y el máximo: con montos de 15.000 a 5.000.000, en escala lineal
 * las franjas chicas no se verían. Vacío si los montos todavía no cuadran.
 */
export function segmentosBarra(b: Borrador): SegmentoBarra[] {
  const n = b.filas.length;
  if (!n) return [];
  const tramos: { letra: string; desde: number; hasta: number }[] = [];
  for (let i = 0; i < n; i++) {
    const d = Number(desdeDe(b, i));
    const h = Number(hastaDe(b, i));
    if (!(d >= 1) || !(h >= d) || !Number.isFinite(d) || !Number.isFinite(h)) return [];
    tramos.push({ letra: b.filas[i].letra, desde: d, hasta: h });
  }
  const pesos = tramos.map((t) => Math.log10(t.hasta + 1) - Math.log10(t.desde));
  const total = pesos.reduce((s, p) => s + p, 0);
  if (!(total > 0)) return [];
  return tramos.map((t, i) => ({ ...t, ancho: (pesos[i] / total) * 100 }));
}

/** «$15.000 CLP – $99.999 CLP». */
export function etiquetaRangoClp(desde: number | null | undefined, hasta: number | null | undefined): string {
  const fmt = (v: number | null | undefined) =>
    v == null || !Number.isFinite(Number(v))
      ? '—'
      : `$${Math.round(Number(v)).toLocaleString('es-CL')} CLP`;
  return `${fmt(desde)} – ${fmt(hasta)}`;
}

// ───────────────────────── Tasa en vivo (sólo para mostrar) ─────────────────────────

export interface TasaEnVivo {
  client: number;
  admin: number;
}

/**
 * Misma fórmula DESCUENTOS del back: desde la tasa base (con comisión) se
 * resta (CLP→VES) o suma (VES→CLP) DESCUENTO_FIAT `n` veces, redondeando a 4
 * decimales en cada paso. Sólo orienta: la oficial es la de la vista previa.
 */
export function tasaEnVivo(
  par: 'CLP-VES' | 'VES-CLP',
  base: number | null | undefined,
  descuentoFiat: number | null | undefined,
  descuentos: number | null | undefined,
): TasaEnVivo | null {
  const x0 = Number(base);
  const d = Number(descuentoFiat);
  const n = Number(descuentos);
  if (base == null || descuentoFiat == null || descuentos == null) return null;
  if (!(x0 > 0) || !Number.isFinite(d) || !Number.isInteger(n) || n < 0 || n > DESCUENTOS_MAX) {
    return null;
  }
  let x = x0;
  for (let i = 0; i < n; i++) x = par === 'CLP-VES' ? round4(x - d) : round4(x + d);
  if (!(x > 0)) return null;
  return { client: x, admin: round4(x - 0.001) };
}

/** Ganancia vs mercado en %, misma cuenta del back. Negativa = paga más que el mercado. */
export function gananciaVsMercado(
  par: 'CLP-VES' | 'VES-CLP',
  tasaCliente: number | null | undefined,
  mid: number | null | undefined,
): number | null {
  const t = Number(tasaCliente);
  const m = Number(mid);
  if (tasaCliente == null || mid == null || !(t > 0) || !(m > 0)) return null;
  return par === 'CLP-VES' ? (1 - t / m) * 100 : (1 - m / t) * 100;
}

/** «100.000 CLP → X VES» o «100.000 VES → X CLP». */
export function ejemplo(
  par: 'CLP-VES' | 'VES-CLP',
  tasaCliente: number | null | undefined,
): string {
  const t = Number(tasaCliente);
  if (tasaCliente == null || !(t > 0)) return '—';
  const fmt = (x: number) =>
    x.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return par === 'CLP-VES'
    ? `100.000 CLP → ${fmt(100_000 * t)} VES`
    : `100.000 VES → ${fmt(100_000 / t)} CLP`;
}

export function motivoValido(motivo: string | null | undefined): boolean {
  const m = (motivo || '').trim();
  return m.length >= MOTIVO_MIN && m.length <= MOTIVO_MAX;
}

// ───────────────────────── Vista previa ─────────────────────────

export interface ComparacionTasa {
  clave: string;
  letra: string;
  desde: number;
  hasta: number;
  clientNuevo: number | null;
  clientActual: number | null;
  adminNuevo: number | null;
  adminActual: number | null;
  margenNuevo: number | null;
  margenActual: number | null;
  /** Diferencia de la tasa Client (nueva − actual). `null` si falta una. */
  delta: number | null;
}

/** Empareja la tasa propuesta con la de hoy por `clave` (franja nueva = sin actual). */
export function compararTasas(
  nuevas: TasaFranja[] | null | undefined,
  actuales: TasaFranja[] | null | undefined,
): ComparacionTasa[] {
  const porClave = new Map((actuales || []).map((t) => [t.clave, t]));
  return [...(nuevas || [])]
    .sort((a, b) => a.desde - b.desde)
    .map((n) => {
      const a = porClave.get(n.clave);
      const cn = numOrNull(n.client);
      const ca = numOrNull(a?.client);
      return {
        clave: n.clave,
        letra: n.letra,
        desde: n.desde,
        hasta: n.hasta,
        clientNuevo: cn,
        clientActual: ca,
        adminNuevo: numOrNull(n.admin),
        adminActual: numOrNull(a?.admin),
        margenNuevo: numOrNull(n.margenPct),
        margenActual: numOrNull(a?.margenPct),
        delta: cn != null && ca != null ? Math.round((cn - ca) * 1e6) / 1e6 : null,
      };
    });
}

function numOrNull(v: any): number | null {
  const n = Number(v);
  return v != null && Number.isFinite(n) ? n : null;
}

/** Tasa de este momento de una franja guardada, por clave. */
export function tasaDeClave(
  tasas: TasaFranja[] | null | undefined,
  clave: string | undefined,
): TasaFranja | null {
  if (!clave) return null;
  return (tasas || []).find((t) => t.clave === clave) || null;
}

// ───────────────────────── Edición en la tabla ─────────────────────────
//
// El «Desde» de la primera franja ES el monto mínimo y el «Hasta» de la última
// ES el monto máximo: se editan ahí mismo. Los «Desde» del medio son de solo
// lectura y se ajustan solos con el «Hasta» anterior.

function aNumero(v: any): number | null {
  return v === '' || v == null ? null : Number(v);
}

/** «Desde» de la primera franja = monto mínimo. */
export function cambiarDesdePrimera(b: Borrador, valor: any): Borrador {
  return { ...b, minimo: aNumero(valor) };
}

/** «Hasta» de la fila `i`; en la última, es el monto máximo. */
export function cambiarHasta(b: Borrador, i: number, valor: any): Borrador {
  if (esUltima(b, i)) return { ...b, maximo: aNumero(valor) };
  const filas = b.filas.map((f, idx) => (idx === i ? { ...f, hasta: aNumero(valor) } : f));
  return { ...b, filas };
}

/** ¿El «Desde» de la fila `i` se edita? Sólo el de la primera (el mínimo). */
export function desdeEditable(i: number): boolean {
  return i === 0;
}

/** Valor a mostrar en el «Hasta» de la fila `i` (en la última, el máximo). */
export function hastaVisible(b: Borrador, i: number): number | null {
  return esUltima(b, i) ? b.maximo : b.filas[i]?.hasta ?? null;
}

/** Error en línea del «Desde» de la fila `i` (sólo la primera se valida). */
export function errorDesdeFila(b: Borrador, i: number): string | null {
  return i === 0 ? errorMinimo(b) : null;
}

/** Error en línea del «Hasta» de la fila `i` (en la última, el del máximo). */
export function errorHastaFila(b: Borrador, i: number): string | null {
  return esUltima(b, i) ? errorMaximo(b) : errorHasta(b, i);
}

export interface EstadoGuardar {
  puedeEditar: boolean;
  hayCambios: boolean;
  motivoValido: boolean;
  errores: number;
  guardando: boolean;
  previsualizando: boolean;
}

/** Guardar sólo con permiso, cambios, motivo, sin errores y sin nada en vuelo. */
export function puedeGuardar(e: EstadoGuardar): boolean {
  return (
    e.puedeEditar &&
    e.hayCambios &&
    e.motivoValido &&
    e.errores === 0 &&
    !e.guardando &&
    !e.previsualizando
  );
}

// ───────────────────────── Resumen de cambios ─────────────────────────

function n0(v: number | null | undefined): string {
  return v == null || !Number.isFinite(Number(v)) ? '—' : Math.round(Number(v)).toLocaleString('es-CL');
}

function rango(d: number | null | undefined, h: number | null | undefined): string {
  return `${n0(d)}–${n0(h)}`;
}

/**
 * Lista corta de lo que cambia frente a lo guardado: mínimo y máximo, franjas
 * nuevas, franjas quitadas, cortes y descuentos movidos. Vacía = sin cambios.
 * `descuentos: false` (motor MARGENES, donde no rigen) los deja fuera.
 */
export function resumenCambios(
  b: Borrador | null,
  inicial: Borrador | null,
  opciones: { descuentos?: boolean } = {},
): string[] {
  if (!b || !inicial) return [];
  const conDescuentos = opciones.descuentos !== false;
  const out: string[] = [];

  if (String(b.minimo) !== String(inicial.minimo)) {
    out.push(`Monto mínimo: ${n0(inicial.minimo)} → ${n0(b.minimo)} CLP`);
  }
  if (String(b.maximo) !== String(inicial.maximo)) {
    out.push(`Monto máximo: ${n0(inicial.maximo)} → ${n0(b.maximo)} CLP`);
  }

  const antes = new Map<string, { i: number; desde: number | null; hasta: number | null; descuentos: number | null }>();
  inicial.filas.forEach((f, i) => {
    if (f.clave) {
      antes.set(f.clave, {
        i,
        desde: desdeDe(inicial, i),
        hasta: hastaDe(inicial, i),
        descuentos: f.descuentos,
      });
    }
  });

  const vistas = new Set<string>();
  b.filas.forEach((f, i) => {
    const d = desdeDe(b, i);
    const h = hastaDe(b, i);
    if (!f.clave) {
      out.push(
        conDescuentos
          ? `Franja nueva ${f.letra}: ${rango(d, h)} CLP, ${n0(f.descuentos)} descuentos`
          : `Franja nueva ${f.letra}: ${rango(d, h)} CLP`,
      );
      return;
    }
    vistas.add(f.clave);
    const a = antes.get(f.clave);
    if (!a) return;
    if (a.desde !== d || a.hasta !== h) {
      out.push(`Franja ${f.letra}: corte ${rango(a.desde, a.hasta)} → ${rango(d, h)}`);
    }
    if (conDescuentos && String(a.descuentos) !== String(f.descuentos)) {
      out.push(`Franja ${f.letra}: descuentos ${n0(a.descuentos)} → ${n0(f.descuentos)}`);
    }
  });

  inicial.filas.forEach((f, i) => {
    if (f.clave && !vistas.has(f.clave)) {
      out.push(`Se quita la franja ${f.letra} (${rango(desdeDe(inicial, i), hastaDe(inicial, i))})`);
    }
  });

  return out;
}

// ───────────────────────── Ejemplo y simulador ─────────────────────────

export const MONTO_EJEMPLO = 100_000;

/**
 * Monto legible: sin decimales desde 1.000 y con dos decimales por debajo.
 * Separador de miles con punto y decimal con coma (es-CL).
 */
export function formatoMonto(x: number | null | undefined): string {
  const n = Number(x);
  if (x == null || !Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) {
    return Math.round(n).toLocaleString('es-CL', { maximumFractionDigits: 0 });
  }
  return n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export interface Pastilla {
  /** Lo que recibe el cliente, ya con moneda: «102.350 VES». */
  recibe: string;
  /** «por 100.000 CLP». */
  por: string;
  /** El número crudo, para cálculos. */
  valor: number;
}

/** Monedas del par: lo que paga el cliente y lo que recibe. */
export function monedasDe(par: 'CLP-VES' | 'VES-CLP'): { paga: string; recibe: string } {
  return par === 'CLP-VES' ? { paga: 'CLP', recibe: 'VES' } : { paga: 'VES', recibe: 'CLP' };
}

/**
 * Cuánto recibe el cliente por `monto` con la tasa client. La tasa es VES por
 * CLP en los dos pares: CLP→VES multiplica, VES→CLP divide.
 */
export function recibeCon(
  par: 'CLP-VES' | 'VES-CLP',
  tasaCliente: number | null | undefined,
  monto: number,
): number | null {
  const t = Number(tasaCliente);
  const m = Number(monto);
  if (tasaCliente == null || !(t > 0) || !(m > 0)) return null;
  return par === 'CLP-VES' ? m * t : m / t;
}

/** Pastilla del ejemplo de la tabla: «102.350 VES» / «por 100.000 CLP». */
export function pastillaEjemplo(
  par: 'CLP-VES' | 'VES-CLP',
  tasaCliente: number | null | undefined,
  monto = MONTO_EJEMPLO,
): Pastilla | null {
  const v = recibeCon(par, tasaCliente, monto);
  if (v == null) return null;
  const m = monedasDe(par);
  return {
    recibe: `${formatoMonto(v)} ${m.recibe}`,
    por: `por ${formatoMonto(monto)} ${m.paga}`,
    valor: v,
  };
}

/** Índice de la franja que contiene `montoClp`, o -1. */
export function indiceFranjaDelMonto(b: Borrador, montoClp: number | null | undefined): number {
  const m = Number(montoClp);
  if (montoClp == null || !Number.isFinite(m)) return -1;
  for (let i = 0; i < b.filas.length; i++) {
    const d = desdeDe(b, i);
    const h = hastaDe(b, i);
    if (d != null && h != null && m >= d && m <= h) return i;
  }
  return -1;
}

export interface Simulacion {
  /** Franja que aplica, o -1. */
  indice: number;
  /** Monto en CLP con el que se ubica la franja (en VES→CLP, convertido al mid). */
  montoClp: number | null;
  /** Mensaje para el operador cuando no aplica ninguna franja. */
  mensaje: string | null;
}

/**
 * Qué franja aplica a un envío de `monto` (en la moneda que paga el cliente).
 * En VES→CLP las franjas están en CLP equivalentes al mid, así que el monto
 * en VES se divide por el mid para ubicarlo.
 */
export function simular(
  par: 'CLP-VES' | 'VES-CLP',
  b: Borrador,
  monto: number | null | undefined,
  mid: number | null | undefined,
): Simulacion {
  const m = Number(monto);
  const moneda = monedasDe(par).paga;
  if (monto == null || !(m > 0)) {
    return { indice: -1, montoClp: null, mensaje: 'Escribe un monto mayor que 0.' };
  }
  let montoClp = m;
  let aMoneda = (clp: number) => clp;
  if (par === 'VES-CLP') {
    const md = Number(mid);
    if (mid == null || !(md > 0)) {
      return {
        indice: -1,
        montoClp: null,
        mensaje: 'No hay precio de mercado para ubicar la franja.',
      };
    }
    montoClp = m / md;
    aMoneda = (clp: number) => clp * md;
  }
  const i = indiceFranjaDelMonto(b, montoClp);
  if (i >= 0) return { indice: i, montoClp, mensaje: null };
  const min = Number(b.minimo);
  const max = Number(b.maximo);
  if (b.minimo != null && b.maximo != null && Number.isFinite(min) && Number.isFinite(max)) {
    return {
      indice: -1,
      montoClp,
      mensaje: `Fuera de rango: mínimo ${formatoMonto(Math.ceil(aMoneda(min)))} ${moneda}, máximo ${formatoMonto(Math.floor(aMoneda(max)))} ${moneda}`,
    };
  }
  return { indice: -1, montoClp, mensaje: 'Revisa el monto mínimo, el máximo y los cortes.' };
}

// ───────────────────────── Paginación del historial ─────────────────────────

export const HISTORIAL_TAMANO = 5;

/** Páginas del historial: ceil(total / tamaño), mínimo 1. */
export function totalPaginas(total: number | null | undefined, tamano = HISTORIAL_TAMANO): number {
  const t = Number(total);
  if (!Number.isFinite(t) || t <= 0 || !(tamano > 0)) return 1;
  return Math.ceil(t / tamano);
}

/** Con 5 cambios o menos no hay paginación. */
export function mostrarPaginacion(
  total: number | null | undefined,
  tamano = HISTORIAL_TAMANO,
): boolean {
  return Number(total) > tamano;
}

/** Página válida entre 1 y el total de páginas. */
export function paginaValida(
  pagina: number,
  total: number | null | undefined,
  tamano = HISTORIAL_TAMANO,
): number {
  const n = Math.floor(Number(pagina));
  const max = totalPaginas(total, tamano);
  if (!Number.isFinite(n) || n < 1) return 1;
  return n > max ? max : n;
}

// ───────────────────────── Resumen para la tarjeta ─────────────────────────

export interface ResumenPar {
  par: 'CLP-VES' | 'VES-CLP';
  franjas: number;
  min: number | null;
  max: number | null;
  respaldo: boolean;
}

/** Resumen de `rates/franjas/estado`: franjas activas por par, mínimo y máximo. */
export function resumenFranjas(estado: any): ResumenPar[] {
  const pares = Array.isArray(estado?.pares) ? estado.pares : [];
  return pares.map((p: any) => ({
    par: p.par,
    franjas: (p.franjas || []).filter((f: any) => f && f.activo).length,
    min: p.limites?.min ?? null,
    max: p.limites?.max ?? null,
    respaldo: p.fuente === 'CONSTANTE',
  }));
}

// ───────────────────────── Errores e historial ─────────────────────────

/**
 * Mensajes del servidor. `handleError` del servicio envuelve el HTTP en
 * `{ message, originalError }`; el 400 de guardar trae `errores: string[]` y
 * el de la validación del DTO trae `message: string[]`.
 */
/** Texto por defecto cuando el back corta con 403 sin mensaje. */
export const TEXTO_SIN_PERMISO = 'Tu usuario no tiene permisos para modificar esta configuración.';

/**
 * En prod, guardar franjas o tramos responde 403 `{ message }` si el correo
 * del operador no está autorizado. Devuelve ese mensaje (o uno por defecto);
 * null si el error no es un 403.
 */
export function mensajeSinPermiso(err: any): string | null {
  if (statusDe(err) !== 403) return null;
  const cuerpo = err?.originalError?.error ?? err?.error ?? null;
  const msg = Array.isArray(cuerpo?.message) ? cuerpo.message.join(' ') : cuerpo?.message;
  return typeof msg === 'string' && msg.trim() && !esTextoDeProtocolo(msg) ? msg.trim() : TEXTO_SIN_PERMISO;
}

/** Status HTTP real del error (envuelto por `handleError` o crudo). */
export function statusDe(err: any): number | undefined {
  const s = err?.originalError?.status ?? err?.status;
  return typeof s === 'number' ? s : undefined;
}

/** Texto por defecto del 409 de concurrencia, según lo que se edita. */
export function textoConflicto(que: 'franjas' | 'tramos'): string {
  return `Otro operador cambió las ${que}. Recarga para ver los cambios.`;
}

/**
 * Guardar con `version` responde 409 si otro operador guardó antes. Devuelve
 * el mensaje del back (o uno por defecto); null si el error no es un 409.
 */
export function mensajeConflicto(err: any, que: 'franjas' | 'tramos'): string | null {
  if (statusDe(err) !== 409) return null;
  const cuerpo = err?.originalError?.error ?? err?.error ?? null;
  const msg = Array.isArray(cuerpo?.message) ? cuerpo.message.join(' ') : cuerpo?.message;
  return typeof msg === 'string' && msg.trim() && !esTextoDeProtocolo(msg) ? msg.trim() : textoConflicto(que);
}

/** La `version` del GET del editor, si vino (campo aditivo del back). */
export function versionDe(r: any): string | null {
  const v = r?.version;
  return typeof v === 'string' && v ? v : null;
}

/** Sin conexión (status 0): la petición no llegó a responder. */
export const TEXTO_SIN_CONEXION = 'No se pudo conectar con el servidor. Revisa tu conexión e intenta de nuevo.';
/** 5xx (504 incluido) sin un mensaje propio del back. */
export const TEXTO_ERROR_SERVIDOR = 'El servidor no pudo completar la operación. Intenta de nuevo en unos segundos.';
export const TEXTO_ERROR_GENERICO = 'No se pudo completar la operación.';

/** Nest manda «Forbidden resource» en los 403 de sus guards; no está en `TEXTO_HTTP_GENERICO`. */
const PROTOCOLO_EXTRA = /^forbidden resource$/i;

/**
 * Texto de protocolo, en inglés, que no es un mensaje del API: «Internal
 * server error», «Unauthorized», «Endpoint request timed out» (API Gateway)…
 * Nunca se le muestra al operador (ver `TEXTO_HTTP_GENERICO`).
 */
export function esTextoDeProtocolo(t: unknown): boolean {
  if (typeof t !== 'string') return false;
  const s = t.trim();
  return TEXTO_HTTP_GENERICO.test(s) || PROTOCOLO_EXTRA.test(s);
}

/** El texto en español para un error sin mensaje propio del back, según su status. */
export function textoErrorHttp(status: number | undefined): string {
  if (status === 401) return TEXTO_SESION_VENCIDA;
  if (status === 0) return TEXTO_SIN_CONEXION;
  if (typeof status === 'number' && status >= 500) return TEXTO_ERROR_SERVIDOR;
  return TEXTO_ERROR_GENERICO;
}

export function erroresDe(err: any): string[] {
  const cuerpo = err?.originalError?.error ?? err?.error ?? null;
  const status = statusDe(err);
  // Sin mensaje propio del back, un 0 (sin conexión), un 401 o un 5xx dicen lo
  // suyo en español (el texto genérico de `handleError` no orienta).
  const porStatus = status === 0 || status === 401 || (typeof status === 'number' && status >= 500);
  const salida: string[] = [];
  if (Array.isArray(cuerpo?.errores)) salida.push(...cuerpo.errores.map(String));
  if (Array.isArray(cuerpo?.message)) salida.push(...cuerpo.message.map(String));
  if (!salida.length) {
    const msg =
      (typeof cuerpo?.message === 'string' && cuerpo.message) ||
      (porStatus ? textoErrorHttp(status) : '') ||
      (typeof err?.message === 'string' && err.message) ||
      TEXTO_ERROR_GENERICO;
    salida.push(msg);
  } else if (typeof cuerpo?.message === 'string' && cuerpo.message) {
    salida.unshift(cuerpo.message);
  }
  // «Internal server error», «Unauthorized», «Endpoint request timed out»…:
  // el texto en español de su status, sin repetirlo.
  const generico = textoErrorHttp(status);
  const limpias = salida.map((t) => (esTextoDeProtocolo(t) ? generico : t));
  return limpias.filter((t, i) => limpias.indexOf(t) === i);
}

function foto(v: any): Record<string, any> | null {
  if (v == null) return null;
  if (typeof v === 'string') {
    try {
      const o = JSON.parse(v);
      return o && typeof o === 'object' ? o : null;
    } catch {
      return null;
    }
  }
  return typeof v === 'object' ? v : null;
}

const CAMPOS_HIST = ['desde', 'hasta', 'modo', 'ajuste', 'activo'];
const CAMPOS_PCT = ['desde', 'hasta', 'activo'];

/**
 * «hasta: 99.999 → 120.000 · descuentos: 2 → 1». En DESCUENTOS el ajuste se
 * rotula «descuentos»; si alguna franja quedó en PCT se muestra la ganancia.
 * Vacío si no hay nada comparable.
 */
export function resumenCambio(h: HistorialFranja): string {
  const antes = foto(h.antes);
  const despues = foto(h.despues);
  if (!despues) return '';

  if (despues['modo'] === 'PCT') {
    const partes: string[] = [];
    CAMPOS_PCT.forEach((k) => {
      if (!(k in despues)) return;
      if (!antes) partes.push(`${k}: ${valorHist(despues[k])}`);
      else if (String(antes[k]) !== String(despues[k])) {
        partes.push(`${k}: ${valorHist(antes[k])} → ${valorHist(despues[k])}`);
      }
    });
    const gNueva = pct(despues['ajuste']);
    if (antes && antes['modo'] === 'PCT') {
      if (String(antes['ajuste']) !== String(despues['ajuste'])) {
        partes.push(`ganancia: ${pct(antes['ajuste'])} → ${gNueva}`);
      }
    } else {
      partes.push(`ganancia: ${gNueva}`);
    }
    return partes.join(' · ');
  }

  // En DESCUENTOS el «ajuste» son los descuentos: se rotula así.
  const rotulo = (k: string) =>
    k === 'ajuste' && despues['modo'] === 'DESCUENTOS' ? 'descuentos' : k;
  if (!antes) {
    return CAMPOS_HIST.filter((k) => k in despues)
      .map((k) => `${rotulo(k)}: ${valorHist(despues[k])}`)
      .join(' · ');
  }
  return CAMPOS_HIST.filter((k) => String(antes[k]) !== String(despues[k]))
    .map((k) => `${rotulo(k)}: ${valorHist(antes[k])} → ${valorHist(despues[k])}`)
    .join(' · ');
}

function pct(v: any): string {
  const n = Number(v);
  if (v == null || !Number.isFinite(n)) return '—';
  return `${n.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} %`;
}

function valorHist(v: any): string {
  if (typeof v === 'boolean') return v ? 'sí' : 'no';
  if (typeof v === 'number' && Number.isInteger(v)) return v.toLocaleString('es-CL');
  return v == null ? '—' : String(v);
}
